import React, { useEffect, useMemo, useState } from 'react';
import { ExerciseService } from '../../../services/ExerciseService';

type ExerciseThumbnailImageProps = Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'> & {
  exerciseId: number;
  exerciseName: string;
  fallbackSrc?: string;
};

const ExerciseThumbnailImage: React.FC<ExerciseThumbnailImageProps> = ({
  exerciseId,
  exerciseName,
  alt,
  fallbackSrc,
  loading = 'lazy',
  decoding = 'async',
  onError,
  ...imgProps
}) => {
  const normalizedFallbackSrc = fallbackSrc?.trim() || null;
  const finalFallbackSrc = useMemo(
    () => normalizedFallbackSrc ?? ExerciseService.getThumbnailFallback(exerciseName),
    [exerciseName, normalizedFallbackSrc],
  );
  const [src, setSrc] = useState(finalFallbackSrc);

  useEffect(() => {
    setSrc(finalFallbackSrc);
  }, [finalFallbackSrc]);

  useEffect(() => {
    let isMounted = true;

    ExerciseService.resolveThumbnail(exerciseId)
      .then((resolvedUrl) => {
        if (!isMounted) {
          return;
        }

        setSrc(resolvedUrl || finalFallbackSrc);
      })
      .catch(() => {
        if (!isMounted) {
          return;
        }

        setSrc(finalFallbackSrc);
      });

    return () => {
      isMounted = false;
    };
  }, [exerciseId, finalFallbackSrc]);

  const handleError = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    onError?.(event);

    if (src !== finalFallbackSrc) {
      setSrc(finalFallbackSrc);
    }
  };

  return (
    <img
      {...imgProps}
      src={src}
      alt={alt ?? `Thumbnail do exercício ${exerciseName}`}
      loading={loading}
      decoding={decoding}
      onError={handleError}
    />
  );
};

export default ExerciseThumbnailImage;